import React, { useState } from 'react'
import {
  Calendar,
  Popover,
  type CalendarProps,
  type SelectedDate,
} from '@opallabs/lapidary'
import {
  displays,
  DatePickerStyleSelectors,
  DatePickerStyleStates,
  type DatePickerDisplayTypes,
  type DisplayProps,
} from './displays'

export { DatePickerStyleSelectors, DatePickerStyleStates }

export type DatePickerProps = Omit<
  CalendarProps,
  'initialSelectedDate' | 'onDateSelect'
> &
  Omit<DisplayProps, 'open' | 'dateDisplayFormat'> & {
    align?: 'start' | 'center' | 'end'
    datePickerDisplayType: DatePickerDisplayTypes
    dateDisplayFormat?: string
    disabled?: boolean
    onDateSelect: (selectedDate: SelectedDate) => void
    onOpenChange?: (open: boolean) => void
    showArrow?: boolean
  }

export const DatePicker: React.FC<DatePickerProps> = ({
  align,
  ariaLabel,
  ariaLive,
  buttonCss,
  buttonText,
  buttonVariant = 'ghost',
  className,
  customFooter,
  dateDisplayFormat = 'MM/dd/yyyy',
  datePickerDisplayType = 'button',
  disabled,
  enableDateRemoval = true,
  htmlId,
  iconPosition,
  initialFocusDate,
  onDateSelect,
  onOpenChange,
  placeholder = 'Select Date ...',
  selectedDate,
  showArrow,
  showButtonText,
  ...calendarProps
}) => {
  const [open, setOpen] = useState(false)
  const handleOpenChange = (open: boolean) => {
    setOpen(open)
    onOpenChange?.(open)
  }

  const Display = displays[datePickerDisplayType]

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger disabled={disabled}>
        <Display
          ariaLabel={ariaLabel}
          ariaLive={ariaLive}
          buttonCss={buttonCss}
          buttonText={buttonText}
          buttonVariant={buttonVariant}
          className={className}
          dateDisplayFormat={dateDisplayFormat}
          disabled={disabled}
          htmlId={htmlId}
          iconPosition={iconPosition}
          open={open}
          placeholder={placeholder}
          selectedDate={selectedDate}
          showButtonText={showButtonText}
        />
      </Popover.Trigger>
      <Popover.Content
        align={align}
        showArrow={showArrow}
        zIndex={9999}
        onEscapeKeyDown={(e) => {
          // Prevent drawers/modals from closing
          e.preventDefault()
          handleOpenChange(false)
        }}
      >
        <Calendar
          {...calendarProps}
          // `customFooter` and `enableDateRemoval` can't be
          // defined together, so we need to be more explicit
          // about these when spreading props on <Calendar>
          {...(customFooter
            ? { customFooter, enableDateRemoval: false }
            : { enableDateRemoval })}
          onDateSelect={(date) => {
            handleOpenChange(false)
            onDateSelect(date)
          }}
          initialFocusDate={selectedDate ?? initialFocusDate}
          initialSelectedDate={selectedDate}
        />
      </Popover.Content>
    </Popover.Root>
  )
}
